import {
  Avatar,
  Chip,
  DateSpan,
  Dialog,
  GenericSuspense,
  IconButton,
  Table,
  useListFilterState,
  useListGraphqlQuery,
  useRuntimeEnv,
  useRuntimeEnvRolesIncludes,
  useTranslation,
  useTranslations,
  useTranslationRoute,
  useUrlParamsValueToString,
  useUserProfilePicture,
  Button,
} from "@harea/component";
import { TTimeOff } from "@harea/component";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useDayOffListSort } from "../../../hooks/useDayOffListSort";
import { MissingListQuery } from "views/dayoff/queries/MissingListQuery";
import { DayOffMissingListQuickFilter } from "../components/DayOffMissingListQuickFilter";
import DayOffMissingListFilter from "../components/DayOffMissingListFilter";
import { useDayOffDeleteMutation } from "views/dayoff/hooks/useDayOffDeleteMutation";
import moment from "moment";

const ProjectDayOffMissingContainer = () => {
  const [
    ADD,
    ADD_IN_BULK,
    MINE,
    EMPLOYEE,
    DAYS,
    DEPARTMENT,
    CREATED_AT,
    ACTIONS,
    CONFIRM,
    CANCEL,
    CONFIRMATION_TITLE,
    MESSAGE_CONFIRMATION,
    ABSENCE,
  ] = useTranslations(i18n);
  const t = useTranslation();

  const navigate = useNavigate();

  const sortItems = useDayOffListSort();
  const [openFilter, setOpenFilter] = useState(false);
  const { filter, setFilter } = useListFilterState(MISSING_DAYOFF_LIST_ID);
  const { runtimeEnv } = useRuntimeEnv();
  const roleIncludes = useRuntimeEnvRolesIncludes(true);
  const isManager = roleIncludes("DASHBOARD_MANAGER");
  const isAdmin = roleIncludes("DASHBOARD_ADMIN_RH");
  const userDepartmentId = (runtimeEnv?.user as any)?.department?.id;
  const personnelDepartmentId = (runtimeEnv?.personnel as any)?.department?.id;
  const departmentId = userDepartmentId || personnelDepartmentId;
  const userPersonnelId = (runtimeEnv?.personnel as any)?.id;
  const userProfilePicture = useUserProfilePicture();
  const tr = useTranslationRoute();
  const urlParamsToString = useUrlParamsValueToString();
  const mutation = useDayOffDeleteMutation();
  const { invalidateQuery } = useListGraphqlQuery(MISSING_DAYOFF_LIST_ID);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const defaultEmployeeFilter = useMemo(() => {
    // Salarié : le backend (TTimeOff.dataFilter) limite déjà la liste à ses absences.
    if (isManager && departmentId) {
      return { department: { id: departmentId } };
    }
    return undefined;
  }, [isManager, departmentId]);

  const onlyMine = filter?.data?.employee?.id === userPersonnelId;

  const resetFilter = () => {
    setFilter((v: any) => ({
      ...v,
      data: {
        ...v.data,
        ...(defaultEmployeeFilter ? { employee: defaultEmployeeFilter } : {}),
      },
    }));
  };

  const handleToggleOnlyMine = () => {
    setFilter((v: any) => ({
      ...v,
      data: {
        ...v.data,
        employee: onlyMine ? defaultEmployeeFilter : { id: userPersonnelId },
      },
    }));
  };

  const confirmDelete = () => {
    if (deleteTarget !== null) {
      mutation.mutate(
        { ids: [deleteTarget] },
        {
          onSuccess: () => {
            invalidateQuery();
            setDeleteTarget(null);
          },
        },
      );
    }
  };

  const canAdd = useMemo(() => {
    return roleIncludes("TIMEOFF_ABSCENCE");
  }, [roleIncludes]);

  const canAddInBulk = useMemo(() => {
    return roleIncludes("TIMEOFF_ABSCENCE");
  }, [roleIncludes]);

  const creationPath = tr("admin/timeoffs/missings/create");

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl">{ABSENCE}</h2>
          <div className="flex gap-3 justify-end">
            {canAddInBulk && (
              <Button
                className="btn btn-primary"
                startIcon="fa-solid fa-plus"
                label={ADD_IN_BULK}
                onClick={() => {
                  const params = new URLSearchParams(urlParamsToString);
                  params.set("bulk", "true");
                  navigate(`${creationPath}?${params.toString()}`);
                }}
              />
            )}
            {canAdd && (
              <Link to={`${creationPath}?${urlParamsToString}`}>
                <Button
                  className="btn btn-primary mr-4"
                  startIcon="fa-solid fa-plus"
                  label={ADD}
                />
              </Link>
            )}
          </div>
        </div>
        <div className="flex gap-4 items-center justify-end">
          {(isManager || isAdmin) && (
            <Chip
              className={onlyMine ? "bg-primary text-white" : "bg-secondary"}
              startIcon={onlyMine ? "fa-check" : "fa-user"}
              label={MINE}
              onClick={handleToggleOnlyMine}
            />
          )}
          <DayOffMissingListQuickFilter
            listId={MISSING_DAYOFF_LIST_ID}
            departmentId={isManager ? departmentId : undefined}
          />
          <IconButton icon="fa fa-filter-circle-xmark" onClick={resetFilter} />
          <IconButton
            icon="fa fa-filter"
            className="text-primary"
            onClick={() => setOpenFilter(!openFilter)}
          />
        </div>

        <Table
          id={MISSING_DAYOFF_LIST_ID}
          BoxProps={{
            className: "box-transparent shadow-none",
          }}
          selection={false}
          search={true}
          sort={{ items: sortItems }}
          pagination={true}
          GQLRequest={MissingListQuery}
          defaultFilter={{
            pageSize: 50,
            page: 0,
            sort: [{ sort: "DESC", value: "createdAt" }],
            data: {
              ...(defaultEmployeeFilter
                ? { employee: defaultEmployeeFilter }
                : {}),
            },
          }}
          columns={useMemo(() => {
            const allColumns: any[] = [
              {
                key: "employee",
                sortKey: "employee.fullName",
                label: EMPLOYEE,
                value: (row: TTimeOff) => (
                  <div className="flex items-center gap-3">
                    <Avatar
                      size="sm"
                      src={userProfilePicture(row.employee?.id!)}
                      fullName={row.employee?.fullName}
                    />
                    <Link
                      to={tr(
                        `admin/timeoffs/missings/${row.id}?${urlParamsToString}`,
                      )}
                    >
                      <span className="text-base font-semibold">
                        {row.employee?.fullName}
                      </span>
                    </Link>
                  </div>
                ),
              },
              {
                key: "days",
                sortKey: "countWorkingDays",
                label: DAYS,
                value: (row: TTimeOff) => (
                  <div className="flex items-center gap-2">
                    <Chip
                      className="bg-default"
                      label={`${row.countWorkingDays} ${t("std_days")}`}
                    />
                    <span className="text-xs text-muted">
                      {moment(row.dateBegin).format("DD/MM")} -{" "}
                      {moment(row.dateEnd).format("DD/MM")}
                    </span>
                  </div>
                ),
              },
              {
                key: "department",
                sortKey: "employee.department.designation",
                label: DEPARTMENT,
                value: (row: TTimeOff) => (
                  <span>{row.employee?.department?.designation}</span>
                ),
              },
              {
                key: "createdAt",
                sortKey: "createdAt",
                label: CREATED_AT,
                value: (row: TTimeOff) => <DateSpan value={row.createdAt} />,
              },
              {
                key: "actions",
                sortable: false,
                label: ACTIONS,
                value: (row: TTimeOff) => (
                  <div className="flex gap-3 items-center">
                    <IconButton
                      icon="fa-solid fa-trash"
                      onClick={() => setDeleteTarget(row.id as number)}
                    />
                  </div>
                ),
              },
            ];

            return allColumns.filter((col) => {
              if (isManager && col.key === "department") return false;
              return true;
            });
          }, [
            isManager,
            EMPLOYEE,
            DAYS,
            DEPARTMENT,
            CREATED_AT,
            ACTIONS,
            t,
            userProfilePicture,
            tr,
            urlParamsToString,
          ])}
        />
      </div>

      <Dialog
        open={deleteTarget !== null}
        confirmation
        title={CONFIRMATION_TITLE}
        message={MESSAGE_CONFIRMATION}
        onConfirm={confirmDelete}
        btnOk={{ label: CONFIRM, isSubmit: mutation.isLoading }}
        btnCancel={{ label: CANCEL }}
        onClose={() => {
          setDeleteTarget(null);
        }}
      />

      <GenericSuspense>
        <Outlet />
      </GenericSuspense>

      <DayOffMissingListFilter
        open={openFilter}
        onClose={() => setOpenFilter(false)}
      />
    </>
  );
};

export default ProjectDayOffMissingContainer;

export const MISSING_DAYOFF_LIST_ID = "dayoff-missing-list-id";
const i18n = [
  "std_add",
  "std_add_in_bulk",
  "BSMRH.Project.DayOff.Management.Mine.Label",
  "std_employee",
  "std_days",
  "std_department",
  "std_created_at",
  "std_actions",
  "std_confirm",
  "std_cancel",
  "std_confirmation",
  "BSMRH.DayOff.Absence.Delete.Confirm.Message",
  "Harea.Project.DayOff.Create.MissingContract.Message",
  "Harea.project.DayOff.LeaveBalance.Label",
  "std_absence",
];
